import { type CreditCardNetwork } from '@wix/cashier-common/dist/src/enums/payments/CreditCardNetwork';
import React, { useContext, type FC, memo, forwardRef, createRef, type RefObject } from 'react';
import { AppStateContext } from '../../../contexts/AppState';
import { DeviceTypeContext } from '../../../contexts/DeviceType';
import { ThemeContext } from '../../../contexts/Theme';
import { type GeneralFieldProps } from '../../../types/Field';
import { FieldName } from '../../../types/CardFieldName';
import { t } from '../../../utils/i18n';
import { FieldErrorMessage } from '../../FieldErrorMessage';
import { FieldLabel } from '../../FieldLabel';
import { FieldInput } from '../../FieldInput';
import { sanitizeCardCVV } from '../../CardForm/utils/sanitizeUtils';
import { getFieldErrorMessage } from '../../../utils/getFieldErrorMessage';
import { DH } from '../../../utils/DataHook';
import { Container, CVVImage, CVVImageRedesign } from './elements';
import { CVVHint } from './elements/CVVHint';
import { getCardCVVSettingsByNetwork } from './utils';
import s from './elements/styles.scss';

type CardCVVProps = GeneralFieldProps & {
  cardNetwork: CreditCardNetwork;
};

export const CardCVV: FC<CardCVVProps> = memo(
  forwardRef((props: CardCVVProps, ref: RefObject<HTMLInputElement>) => {
    const {
      value,
      isTouched,
      isValid,
      isFinished,
      isErrorCanBeShown,
      cardNetwork,
      customInput,

      onChange,
      onBlur,
      onFocus,
      onLabelClick,
    } = props;

    ref = ref || createRef();
    const inputSettings = getCardCVVSettingsByNetwork(cardNetwork, customInput);
    const deviceType = useContext(DeviceTypeContext);
    const { isSavedCard } = useContext(AppStateContext);
    const theme = useContext(ThemeContext);
    const focusInput = () => {
      ref.current?.focus();
    };

    if (!customInput) {
      return (
        <div data-hook={DH.Field.Container.CVV}>
          <FieldInput
            ref={ref}
            required
            autoComplete="cc-csc"
            fieldName={FieldName.CVV}
            label={t('card.cvv.cvc')}
            maxLength={inputSettings.inputSize}
            placeholder={t(inputSettings.placeholder)}
            type="tel"
            pattern="^\d+$"
            value={sanitizeCardCVV(value)}
            suffix={<CVVImageRedesign src={inputSettings.helperImageURL} onClick={focusInput} />}
            isTouched={isTouched}
            isValid={isValid}
            isErrorCanBeShown={isErrorCanBeShown}
            errorMessage={getFieldErrorMessage({
              fieldName: FieldName.CVV,
              isFinished,
              isEmpty: !value,
              i18nParams: { cvvLabel: t('card.cvv.cvc') },
            })}
            onChange={onChange}
            onFocus={onFocus}
            onBlur={onBlur}
          />
        </div>
      );
    }

    return (
      <Container>
        <FieldLabel
          fieldName={FieldName.CVV}
          text={t(theme === 'wsr-like' ? 'card.cvv' : 'card.securityCode')}
          className={s.label}
          onClick={onLabelClick}
        />
        <FieldInput
          ref={ref}
          customInput
          fieldName={FieldName.CVV}
          containerClassName={s.input}
          size={inputSettings.inputSize}
          maxLength={inputSettings.inputSize}
          placeholder={inputSettings.placeholder}
          type="tel"
          pattern="^\d+$"
          value={sanitizeCardCVV(value)}
          isValid={isValid}
          isTouched={isTouched}
          required
          name="cvc"
          autoComplete="cc-csc"
          iconRight={
            deviceType === 'desktop' && isSavedCard ? (
              <CVVImage fillSize src={inputSettings.helperImageURL} onClick={focusInput} />
            ) : null
          }
          onChange={onChange}
          onFocus={onFocus}
          onBlur={onBlur}
        />
        {deviceType === 'desktop' && !isSavedCard && (
          <CVVHint text={t(inputSettings.helperTextKey)} cvvImageSrc={inputSettings.helperImageURL} />
        )}
        {deviceType === 'mobile' && <CVVImage src={inputSettings.helperImageURL} onClick={focusInput} />}
        {isTouched && !isValid && <FieldErrorMessage fieldName={FieldName.CVV} />}
      </Container>
    );
  }),
);
CardCVV.displayName = 'CardCVV';
